import { useCallback, useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { useTranslation } from "react-i18next";
import SEO from "../components/site/SEO";
import { HOLIDAYS_PATH, HOLIDAY_PACKAGES, HOLIDAY_IMAGES } from "../data/holidays";
import { HolidayScrollContext } from "../components/holidays/shared";
import HolidayHero from "../components/holidays/HolidayHero";
import HolidayIntro from "../components/holidays/HolidayIntro";
import HolidayPackages from "../components/holidays/HolidayPackages";
import HolidayEarlyBird from "../components/holidays/HolidayEarlyBird";
import HolidayIncluded from "../components/holidays/HolidayIncluded";
import HolidayRiding from "../components/holidays/HolidayRiding";
import HolidayBike from "../components/holidays/HolidayBike";
import HolidayStay from "../components/holidays/HolidayStay";
import HolidayMore from "../components/holidays/HolidayMore";
import { HolidayCompanions, HolidayTransfers } from "../components/holidays/HolidayCompanionsTransfers";
import { HolidaySeason, HolidayPrivate } from "../components/holidays/HolidaySeasonPrivate";
import { HolidayB2B, HolidayFinalCTA } from "../components/holidays/HolidayB2BFinal";
import HolidayDepartures from "../components/holidays/HolidayDepartures";
import HolidayFAQ from "../components/holidays/HolidayFAQ";
import HolidayEnquiryForm from "../components/holidays/HolidayEnquiryForm";
import HolidayStickyBar from "../components/holidays/HolidayStickyBar";

const SITE = "https://apuseniactive.ro";

function buildJsonLd(t) {
  const abs = (p) => SITE + p;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TouristTrip",
        "@id": abs(HOLIDAYS_PATH) + "#trip",
        name: "Apuseni E-MTB Escape 2027",
        description: t("holidays.meta.description"),
        url: abs(HOLIDAYS_PATH),
        image: [abs(HOLIDAY_IMAGES.hero), abs(HOLIDAY_IMAGES.intro), abs(HOLIDAY_IMAGES.bike.rocks)],
        touristType: ["Mountain bikers", "E-bike riders", "Active travellers"],
        itinerary: {
          "@type": "Place",
          name: "Apuseni Mountains",
          address: { "@type": "PostalAddress", addressRegion: "Bihor", addressCountry: "RO" },
        },
        provider: { "@id": SITE + "/#organization" },
        offers: HOLIDAY_PACKAGES.map((p) => ({
          "@type": "Offer",
          name: `Apuseni E-MTB Escape — ${t(`holidays.packages.items.${p.key}.duration`)}`,
          price: p.price,
          priceCurrency: "EUR",
          priceSpecification: { "@type": "UnitPriceSpecification", price: p.price, priceCurrency: "EUR", unitText: "per person" },
          availability: "https://schema.org/PreOrder",
          validFrom: "2027-06-01",
          validThrough: "2027-09-30",
          url: abs(HOLIDAYS_PATH) + "#packages",
        })),
      },
      {
        "@type": "FAQPage",
        "@id": abs(HOLIDAYS_PATH) + "#faq",
        mainEntity: t("holidays.faq.items", { returnObjects: true }).map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Apuseni Active", item: SITE + "/" },
          { "@type": "ListItem", position: 2, name: "Apuseni E-MTB Holidays 2027", item: abs(HOLIDAYS_PATH) },
        ],
      },
    ],
  };
}

export default function HolidaysPage() {
  const { t } = useTranslation();
  const lenisRef = useRef(null);
  const [preset, setPreset] = useState(null);

  // Smooth momentum scrolling, scoped to this page only.
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, []);

  const scrollTo = useCallback((id, pkg) => {
    if (pkg) setPreset(pkg);
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -72, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <HolidayScrollContext.Provider value={scrollTo}>
      <div className="bg-bone" data-testid="holidays-page">
        <SEO titleKey="holidays.title" descKey="holidays.description" path={HOLIDAYS_PATH} image={HOLIDAY_IMAGES.hero} extraJsonLd={buildJsonLd(t)} />
        <HolidayHero />
        <HolidayIntro />
        <HolidayPackages />
        <HolidayEarlyBird />
        <HolidayIncluded />
        <HolidayRiding />
        <HolidayBike />
        <HolidayStay />
        <HolidayMore />
        <HolidayCompanions />
        <HolidayTransfers />
        <HolidaySeason />
        <HolidayDepartures />
        <HolidayPrivate />
        <HolidayB2B />
        <HolidayFAQ />
        <HolidayFinalCTA />
        <HolidayEnquiryForm preset={preset} />
        <HolidayStickyBar />
      </div>
    </HolidayScrollContext.Provider>
  );
}
